/**
 * Telemetría del upsell (contrato 3.2). Una `UpsellVisit` por entrada en el
 * paso: guarda el capital con el que llega el cliente (el preseleccionado), las
 * tarjetas que ve, cuándo las vio, cuántas veces cambió de capital (tarjetas y
 * capital libre), las pulsaciones de − / + y cómo llegó a lo que tiene elegido
 * (`via`). Con eso emite `upsell_viewed`, `upsell_option_selected`,
 * `upsell_custom_applied` y, al continuar, la decisión (`upsell_accepted` o
 * `upsell_declined`). Sin React: las pantallas la crean al montar y le cuentan
 * lo que pasa.
 *
 * Solo capitales, precios y contadores; nunca un dato del cliente. Como todo el
 * tracking, nunca rompe la app.
 */
import type { UpsellCustomResult, UpsellDirection, UpsellPosition, UpsellVia } from "./events";
import { track } from "./index";

/** Lo que define la opción elegida en el upsell: capital, cobertura y capital por garantía. */
export interface UpsellChoiceLike {
  capital?: number | null;
  coverage?: string | null;
  priceToPay?: number | null;
  regularPrice?: number | null;
  period?: string | null;
  capitalsByTrait?: Record<string, number> | null;
  capitals?: unknown;
}

const isCapital = (value: unknown): value is number =>
  "number" === typeof value && Number.isFinite(value);

const traitCapitals = (choice: UpsellChoiceLike): Record<string, number | null> => {
  const out: Record<string, number | null> = {};
  if (choice.capitalsByTrait) {
    Object.keys(choice.capitalsByTrait)
      .sort()
      .forEach((trait) => {
        out[trait] = choice.capitalsByTrait![trait] ?? null;
      });
    return out;
  }
  if (choice.capitals && typeof choice.capitals === "object") {
    const capitals = choice.capitals as Record<string, { capital?: unknown } | null | undefined>;
    Object.keys(capitals)
      .sort()
      .forEach((trait) => {
        const capital = capitals[trait]?.capital;
        out[trait] = typeof capital === "number" ? capital : null;
      });
  }
  return out;
};

/**
 * Firma de la opción del upsell. No incluye el precio: cambiar la fecha de
 * efecto cambia el precio, pero no es aceptar una mejora.
 */
export const upsellChoiceSignature = (choice: UpsellChoiceLike): string =>
  JSON.stringify({
    capital: choice.capital ?? null,
    coverage: choice.coverage ?? null,
    traits: traitCapitals(choice),
  });

/**
 * El capital de la opción. Con capital por garantía (ES, AXA…), el de
 * fallecimiento, como en el resto de la app; si no, `capital`.
 */
export const upsellCapitalOf = (choice: UpsellChoiceLike | null | undefined): number | null => {
  if (!choice) return null;
  if (choice.capitals && typeof choice.capitals === "object") {
    const death = (choice.capitals as Record<string, { capital?: unknown } | null | undefined>).DEATH
      ?.capital;
    if (isCapital(death)) return death;
  }
  const byTrait = choice.capitalsByTrait?.DEATH;
  if (isCapital(byTrait)) return byTrait;
  return isCapital(choice.capital) ? choice.capital : null;
};

/** Las tarjetas de al lado del capital inicial: la mayor por debajo y la menor por encima. */
export const neighbourCapitals = (
  initial: number | null,
  cards: ReadonlyArray<number | null>
): { lower: number | null; upper: number | null } => {
  let lower: number | null = null;
  let upper: number | null = null;
  if (!isCapital(initial)) return { lower, upper };
  cards.forEach((capital) => {
    if (!isCapital(capital)) return;
    if (capital < initial && (null === lower || capital > lower)) lower = capital;
    if (capital > initial && (null === upper || capital < upper)) upper = capital;
  });
  return { lower, upper };
};

/** Dónde queda un capital respecto al inicial; `other` si no se sabe alguno de los dos. */
export const upsellPosition = (capital: number | null, initial: number | null): UpsellPosition => {
  if (!isCapital(capital) || !isCapital(initial)) return "other";
  if (capital < initial) return "lower";
  if (capital > initial) return "upper";
  return "initial";
};

/** Sube, baja o se queda igual respecto al inicial; null si no se sabe alguno de los dos. */
export const upsellDirection = (
  capital: number | null,
  initial: number | null
): UpsellDirection | null => {
  if (!isCapital(capital) || !isCapital(initial)) return null;
  if (capital > initial) return "up";
  if (capital < initial) return "down";
  return "same";
};

/** Lo que lleva la decisión además de la opción elegida (3.2). */
export interface UpsellDecisionContext {
  initialCapital: number | null;
  via: UpsellVia;
  changes: number;
  stepperClicks: number;
  /** Desde `upsell_viewed`. */
  timeMs: number | null;
}

/**
 * Al continuar desde el upsell: `upsell_accepted` si el cliente cambió de
 * capital o cobertura respecto a lo preseleccionado al entrar, y
 * `upsell_declined` si sigue con lo mismo (aunque lo cambiara y volviera).
 */
export const trackUpsellDecision = (
  baselineSignature: string | null,
  choice: UpsellChoiceLike | null | undefined,
  context: UpsellDecisionContext
): void => {
  if (!choice || baselineSignature === null) return;
  const decision = {
    initial_capital: context.initialCapital,
    via: context.via,
    changes: context.changes,
    stepper_clicks: context.stepperClicks,
    time_ms: context.timeMs,
  };
  if (upsellChoiceSignature(choice) === baselineSignature) {
    track("upsell_declined", decision);
    return;
  }
  const capital = upsellCapitalOf(choice);
  track("upsell_accepted", {
    capital,
    price: choice.priceToPay ?? choice.regularPrice ?? null,
    period: choice.period ? String(choice.period).toLowerCase() : null,
    ...decision,
    direction: upsellDirection(capital, context.initialCapital),
  });
};

export interface UpsellViewInput {
  /** El capital preseleccionado, con el que llega. */
  initialCapital: number | null;
  /** Capitales de las tarjetas que ve; ninguna si el upsell no tiene tarjetas. */
  cards?: ReadonlyArray<number | null>;
  hasPromotion: boolean;
  /** Lo preseleccionado, para decidir al continuar. Por defecto, solo el capital inicial. */
  baseline?: UpsellChoiceLike;
}

export interface UpsellCustomInput {
  /** Lo que escribió el cliente, o null si no es un número. */
  requestedCapital: number | null;
  /** El capital que queda aplicado. Sin él, el que ya estaba. */
  capital?: number | null;
  result: UpsellCustomResult;
}

/** El seguimiento del upsell en una entrada al paso. */
export class UpsellVisit {
  private readonly now: () => number;
  private viewedAt: number | null = null;
  private baseline: string | null = null;
  private initial: number | null = null;
  private shownCards: number[] = [];
  /** El capital de la selección actual. */
  private current: number | null = null;
  private changeCount = 0;
  private stepperCount = 0;
  private lastVia: UpsellVia = "initial";

  constructor(now: () => number = () => Date.now()) {
    this.now = now;
  }

  get viewed(): boolean {
    return null !== this.viewedAt;
  }

  get initialCapital(): number | null {
    return this.initial;
  }

  get cards(): readonly number[] {
    return this.shownCards;
  }

  get changes(): number {
    return this.changeCount;
  }

  get stepperClicks(): number {
    return this.stepperCount;
  }

  get via(): UpsellVia {
    return this.lastVia;
  }

  /** `upsell_viewed`, una vez por entrada: las llamadas siguientes (recargas de datos) no hacen nada. */
  view(input: UpsellViewInput): void {
    try {
      if (this.viewed) return;
      this.viewedAt = this.now();
      this.initial = isCapital(input.initialCapital) ? input.initialCapital : null;
      this.current = this.initial;
      const cards = input.cards ?? [];
      this.shownCards = cards.filter(isCapital);
      this.baseline = upsellChoiceSignature(input.baseline ?? { capital: this.initial });
      const { lower, upper } = neighbourCapitals(this.initial, this.shownCards);
      track("upsell_viewed", {
        has_promotion: input.hasPromotion,
        initial_capital: this.initial,
        lower_capital: lower,
        upper_capital: upper,
        options: cards.length,
      });
    } catch {
      /* el tracking nunca rompe la app */
    }
  }

  /** Pulsa una tarjeta que no era la elegida. `price`: el mensual que enseña la tarjeta. */
  optionSelected(card: { capital: number | null; price: number | null }): void {
    try {
      this.changed("option", card.capital);
      track("upsell_option_selected", {
        capital: card.capital,
        price: card.price,
        position: upsellPosition(card.capital, this.initial),
      });
    } catch {
      /* noop */
    }
  }

  /** Una pulsación de − o + del capital libre: solo cuenta, sin evento. */
  stepperClicked(): void {
    this.stepperCount += 1;
  }

  /**
   * Un «aplicar» del capital libre. Cuenta como cambio si deja un capital
   * aplicado (`applied`, `adjusted`) o si mueve la selección (un `error` que la
   * deja en otra tarjeta).
   */
  customApplied(input: UpsellCustomInput): void {
    try {
      const capital = undefined === input.capital ? this.current : input.capital;
      const moved = isCapital(capital) && capital !== this.current;
      if ("applied" === input.result || "adjusted" === input.result || moved) {
        this.changed("custom", capital);
      }
      track("upsell_custom_applied", {
        requested_capital: input.requestedCapital,
        capital,
        result: input.result,
      });
    } catch {
      /* noop */
    }
  }

  /**
   * Upsells con capital por garantía: el capital libre es el de fallecimiento.
   * Devuelve el que pide este recálculo si lo cambia, o null si no (activar o
   * quitar una garantía, o cambiar el capital de otra).
   */
  capitalRequest(capitals: Record<string, number> | null | undefined): number | null {
    const requested = capitals?.DEATH;
    return isCapital(requested) && requested !== this.current ? requested : null;
  }

  /**
   * Cómo acabó el recálculo de un `capitalRequest`: con la opción que devuelve
   * el servidor (`applied` si es la pedida, `adjusted` si no) o null si falló
   * (`error`). No hace nada si el recálculo no cambiaba el capital.
   */
  capitalRecalculated(requested: number | null, result: UpsellChoiceLike | null): void {
    if (null === requested) return;
    if (!result) {
      this.customApplied({ requestedCapital: requested, result: "error" });
      return;
    }
    const capital = upsellCapitalOf(result);
    this.customApplied({
      requestedCapital: requested,
      capital,
      result: capital === requested ? "applied" : "adjusted",
    });
  }

  /** Continúa: la decisión, con lo que pasó en esta entrada. Sin `upsell_viewed` no emite nada. */
  decide(choice: UpsellChoiceLike | null | undefined): void {
    try {
      trackUpsellDecision(this.baseline, choice, {
        initialCapital: this.initial,
        via: this.lastVia,
        changes: this.changeCount,
        stepperClicks: this.stepperCount,
        timeMs: null === this.viewedAt ? null : Math.max(0, this.now() - this.viewedAt),
      });
    } catch {
      /* noop */
    }
  }

  private changed(via: UpsellVia, capital: number | null): void {
    this.changeCount += 1;
    this.lastVia = via;
    this.current = capital;
  }
}
